import { useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBus,
  FaClock,
  FaChevronLeft,
  FaSlidersH,
  FaPen,
  // FaStar, // Removed FaStar
} from "react-icons/fa";
import { TbSunrise, TbSun, TbSunset, TbMoon } from "react-icons/tb";

import {
  // shared context + helpers from _core (contract listed below)
  useSearchCore,
  PALETTE,
  calculateDuration,
  getDisplayPrice,
  getReadableDate,
  TIME_SLOTS, // ✅ import TIME_SLOTS for chips
} from "./_core";

// mobile-only leaf components
import BookingDeadlineTimer from "./components/BookingDeadlineTimer";
import FilterPanel from "./components/FilterPanel";
import SpecialNoticesSection from "./components/SpecialNoticesSection";
import MobileBottomSheet from "./components/MobileBottomSheet";
import MobileSearchSheet from "./components/MobileSearchSheet";
import MobileCityPicker from "./components/MobileCityPicker";
import MobileCalendarSheet from "./components/MobileCalendarSheet";

// ————————————————————————————————
// Local tiny skeleton used during loading
const BusCardSkeleton = () => (
  <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #F5F5F5", boxShadow: "0 1px 6px rgba(0,0,0,0.025)", padding: "16px", marginBottom: 10, overflow: "hidden" }} className="animate-pulse">
    <div style={{ height: 3, background: "#F3F3F3", borderRadius: 2, marginBottom: 14 }} />
    <div className="flex justify-between items-start">
      <div style={{ flex: 1, paddingRight: 12 }}>
        <div style={{ height: 14, width: "55%", background: "#F0F0F0", borderRadius: 6, marginBottom: 8 }} />
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ height: 20, width: 36, background: "#F0F0F0", borderRadius: 6 }} />
          <div style={{ height: 20, width: 60, background: "#F0F0F0", borderRadius: 6 }} />
        </div>
      </div>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F0F0F0" }} />
    </div>
    <div style={{ height: 1, background: "#F3F3F3", margin: "14px 0" }} />
    <div className="flex items-center justify-between">
      <div style={{ height: 20, width: 44, background: "#F0F0F0", borderRadius: 6 }} />
      <div style={{ flex: 1, height: 1, background: "#F0F0F0", margin: "0 12px" }} />
      <div style={{ height: 20, width: 44, background: "#F0F0F0", borderRadius: 6 }} />
    </div>
    <div style={{ height: 1, background: "#F3F3F3", margin: "14px 0" }} />
    <div className="flex justify-between items-center">
      <div style={{ height: 22, width: "50%", background: "#F0F0F0", borderRadius: 6 }} />
      <div style={{ height: 22, width: 60, background: "#FEF2F2", borderRadius: 6 }} />
    </div>
  </div>
);

// simple fade/slide
const listVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

// minimal icon per time slot (redbus-like)
const slotIcon = (slot) => {
  if (/morning/i.test(slot)) return TbSunrise;
  if (/afternoon/i.test(slot)) return TbSun;
  if (/evening/i.test(slot)) return TbSunset;
  if (/night/i.test(slot)) return TbMoon;
  return TbSun;
};

const buildDetailsLine = (bus, fallbackSeatCount) => {
  const parts = [
    (bus?.vehicle?.seatCount || fallbackSeatCount)
      ? `${bus?.vehicle?.seatCount || fallbackSeatCount} Seater`
      : "",
    bus?.facilities?.ac || bus?.busType === "AC" ? "AC" : "",
    bus?.features?.chargingPort || bus?.facilities?.usb ? "USB Charging" : "",
    bus?.facilities?.recliner ? "Recliner Seats" : "",
    bus?.features?.wifi ? "WiFi" : "",
    bus?.facilities?.gps ? "GPS" : "",
    bus?.facilities?.blanket ? "Blanket" : "",
    bus?.facilities?.water ? "Water" : "",
  ].filter(Boolean);

  return parts.join(" • ");
};

export default function Mobile() {
  const nav = useNavigate();
  const {
    from,
    to,
    searchDate,
    setSearchDate,
    searchDateParam,
    todayStr,
    loading,
    fetchError,
    // sortedBuses, // not used directly in renderCards
    visibleBuses,
    availability,

    // card open/close
    // expandedBusId, // not used in Mobile card render
    handleToggleSeatLayout,

    // filters + sort
    isFilterOpen,
    setIsFilterOpen,
    activeFilterCount,
    sortBy,
    setSortBy,
    filters,
    setFilters,

    // pickers/sheets
    // mobileSearchOpen, // not used directly, only setter
    setMobileSearchOpen,
    mobilePickerOpen,
    setMobilePickerOpen,
    mobilePickerMode,
    setMobilePickerMode,
    calOpen,
    setCalOpen,

    fromOptions,
    toOptions,
    recent,

    // navigation search update
    updateSearchWithDate,
    fetchData,
    refreshAvailability,
    handleMobilePick,
  } = useSearchCore();

  // hidden native <input type="date"> for the header chip quick change
  const mobileDateInputRef = useRef(null);
  // const openDateNative = () => mobileDateInputRef.current?.showPicker(); // Removed as we use the calendar sheet
  const onNativeDateChange = (e) => {
    const d = e.target.value;
    setSearchDate(d);
    updateSearchWithDate(d);
  };

  const activeIsSoldOut = (busKey, bus) => {
    const a = availability?.[busKey];
    const available =
      typeof a?.available === "number"
        ? a.available
        : typeof bus?.availableSeats === "number"
        ? bus.availableSeats
        : null;
    return typeof available === "number" && available === 0;
  };

  const timeSlotKeys = useMemo(() => Object.keys(TIME_SLOTS), []);

  const getCardAvailability = useCallback(
    (busKey, bus) => {
      const a = availability?.[busKey];
      const availableSeats =
        typeof a?.available === "number"
          ? a.available
          : typeof bus?.availableSeats === "number"
          ? bus.availableSeats
          : null;
      const availableWindowSeats =
        typeof a?.window === "number"
          ? a.window
          : typeof bus?.availableWindowSeats === "number"
          ? bus.availableWindowSeats
          : null;

      return { a, availableSeats, availableWindowSeats };
    },
    [availability]
  );

  // --------------------------------------------------------------------------------------
  // RENDER CARDS (UPDATED UI)
  // --------------------------------------------------------------------------------------
  const renderCards = () => {
    if (loading) {
      return Array.from({ length: 5 }).map((_, i) => (
        <BusCardSkeleton key={i} />
      ));
    }
    if (fetchError) {
      return (
        <div style={{ textAlign: "center", padding: "32px 16px", background: "#fff", borderRadius: 16, border: "1px solid #F5F5F5", boxShadow: "0 1px 6px rgba(0,0,0,0.025)" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 6 }}>
            Oops! Something went wrong.
          </div>
          <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>
            {fetchError}
          </div>
          <button
            onClick={fetchData}
            style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: "#DC2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 14px rgba(220,38,38,0.25)" }}
          >
            Try again
          </button>
        </div>
      );
    }
    if (!visibleBuses.length) {
      return (
        <div style={{ textAlign: "center", padding: "32px 16px", background: "#fff", borderRadius: 16, border: "1px solid #F5F5F5", boxShadow: "0 1px 6px rgba(0,0,0,0.025)" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#111", marginBottom: 6 }}>
            {activeFilterCount ? "No Buses Match Your Filters" : "No Buses Available"}
          </div>
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>
            {activeFilterCount
              ? "Try adjusting or resetting your filters."
              : "No buses were found for this route on the selected date."}
          </div>
        </div>
      );
    }

    return (
      <motion.div
        variants={listVariants}
        initial="hidden"
        animate="visible"
        className="space-y-3"
      >
        {visibleBuses.map((bus) => {
          const busKey = `${bus._id}-${bus.departureTime}`;
          const displayPrice = getDisplayPrice(bus, from, to);

          let timerProps = null;
          if (searchDateParam && bus.departureTime) {
            const now = new Date();
            const [depH, depM] = bus.departureTime.split(":").map(Number);
            // searchDateParam is YYYY-MM-DD
            const [yy, mm, dd] = searchDateParam.split("-").map(Number);
            // Date constructor: year, monthIndex (0-11), day, hour, minute
            const dep = new Date(yy, mm - 1, dd, depH, depM).getTime();
            const diffHrs = (dep - now.getTime()) / (1000 * 60 * 60);
            // Show timer if departure is within the next 12 hours
            if (diffHrs > 0 && diffHrs <= 12) {
              // Set deadline 1 hour before departure
              timerProps = {
                deadlineTimestamp: dep - 60 * 60 * 1000,
                departureTimestamp: dep,
                onDeadline: () => refreshAvailability([bus], { force: true }), // Refresh only this bus on deadline
              };
            }
          }

          const { availableSeats, availableWindowSeats } =
            getCardAvailability(busKey, bus);
          const hasAvailability = typeof availableSeats === "number";
          const canBook =
            bus?.canBook !== false && bus?.listingMode !== "timetableOnly";
          const seatCountToShow = canBook
            ? hasAvailability
              ? availableSeats
              : Number(bus?.vehicle?.seatCount || 0)
            : Number(bus?.vehicle?.seatCount || 0);
          const detailsLine = buildDetailsLine(bus, seatCountToShow);
          const isSoldOut = activeIsSoldOut(busKey, bus);
          const hasStrike =
            typeof bus.originalPrice === "number" &&
            bus.originalPrice > displayPrice;

          return (
            <motion.div
              key={busKey}
              variants={itemVariants}
              style={{ marginBottom: 10 }}
            >
              <button
                type="button"
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  borderRadius: 16,
                  border: "1px solid #F5F5F5",
                  boxShadow: "0 1px 8px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(0,0,0,0.03)",
                  overflow: "hidden",
                  opacity: isSoldOut ? 0.55 : 1,
                  cursor: isSoldOut ? "not-allowed" : "pointer",
                  display: "block",
                  padding: 0,
                  WebkitTapHighlightColor: "transparent",
                }}
                onClick={() => !isSoldOut && handleToggleSeatLayout(bus)}
                disabled={isSoldOut}
              >
                <div style={{ padding: "15px 15px 14px" }}>

                  {/* ── Status badge ── */}
                  <div style={{ marginBottom: 10 }}>
                    {bus.listingMode === "timetableOnly" ? (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C74A50", display: "inline-block" }} />
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#C74A50", letterSpacing: "0.02em" }}>Timetable Only</span>
                      </div>
                    ) : (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16A34A", display: "inline-block" }} />
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#16A34A", letterSpacing: "0.02em" }}>Online booking available</span>
                      </div>
                    )}
                  </div>

                  {/* ── Row 1: Name + pills + logo ── */}
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: "#111", margin: "0 0 7px", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {bus.name}
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                        {bus.busType ? (
                          <span style={{ fontSize: 10, fontWeight: 500, color: "#2563EB", background: "#EFF6FF", borderRadius: 5, padding: "2px 7px" }}>
                            {bus.busType}
                          </span>
                        ) : null}
                        {typeof seatCountToShow === "number" && (seatCountToShow > 0 || (canBook && hasAvailability && seatCountToShow === 0)) ? (
                          <span style={{ fontSize: 10, fontWeight: 500, color: "#DC2626", background: "#FEF2F2", borderRadius: 5, padding: "2px 7px" }}>
                            {canBook
                              ? hasAvailability
                                ? seatCountToShow === 0 ? "Sold Out" : `${seatCountToShow} Seats Left`
                                : `${seatCountToShow} Seats`
                              : `${seatCountToShow} Seats`}
                          </span>
                        ) : null}
                        {canBook && typeof availableWindowSeats === "number" && availableWindowSeats > 0 ? (
                          <span style={{ fontSize: 10, fontWeight: 500, color: "#555", background: "#F5F5F5", borderRadius: 5, padding: "2px 7px" }}>
                            {availableWindowSeats} Window
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {/* Logo */}
                    {bus.operatorLogo ? (
                      <div style={{ width: 42, height: 42, borderRadius: 12, border: "1px solid #F0F0F0", background: "#FAFAFA", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <img
                          src={bus.operatorLogo}
                          alt={`${bus.name} logo`}
                          style={{ width: 30, height: 30, objectFit: "contain" }}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    ) : (
                      <div style={{ width: 42, height: 42, borderRadius: 12, border: "1px solid #F0F0F0", background: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FaBus style={{ fontSize: 15, color: "#D1D5DB" }} />
                      </div>
                    )}
                  </div>

                  {/* ── Divider ── */}
                  <div style={{ height: 1, background: "#F7F7F7", marginBottom: 12 }} />

                  {/* ── Row 2: Time strip ── */}
                  <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 12 }}>
                    {/* Departure */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#111", letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {bus.departureTime}
                      </div>
                      <div style={{ fontSize: 9, color: "#B0B7C3", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 500 }}>
                        Departure
                      </div>
                    </div>

                    {/* Duration connector */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 10px" }}>
                      <div style={{ fontSize: 10, fontWeight: 500, color: "#DC2626", background: "rgba(220,38,38,0.055)", borderRadius: 20, padding: "2px 9px", marginBottom: 5, letterSpacing: "0.01em" }}>
                        {calculateDuration(bus.departureTime, bus.arrivalTime)}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <div style={{ width: 4, height: 4, borderRadius: "50%", border: "1.5px solid #E2E4E9", background: "#fff", flexShrink: 0 }} />
                        <div style={{ flex: 1, height: 1, background: "repeating-linear-gradient(90deg, #E2E4E9 0px, #E2E4E9 3px, transparent 3px, transparent 7px)" }} />
                        <div style={{ width: 4, height: 4, borderRadius: "50%", border: "1.5px solid #E2E4E9", background: "#fff", flexShrink: 0 }} />
                      </div>
                    </div>

                    {/* Arrival */}
                    <div style={{ minWidth: 0, textAlign: "right" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#111", letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {bus.arrivalTime}
                      </div>
                      <div style={{ fontSize: 9, color: "#B0B7C3", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 500 }}>
                        Arrival
                      </div>
                    </div>
                  </div>

                  {/* ── Divider ── */}
                  <div style={{ height: 1, background: "#F7F7F7", marginBottom: 12 }} />

                  {/* ── Row 3: Details chips + countdown + price ── */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    {/* Details chips + countdown */}
                    <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      {detailsLine ? (
                        <div className="rb-mobile-chip-scroll" style={{ display: "flex", alignItems: "center", gap: 4, overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap", WebkitOverflowScrolling: "touch", paddingBottom: 1 }}>
                          {detailsLine.split(" • ").map((chip, i) => (
                            <span key={i} style={{ height: 24, display: "inline-flex", alignItems: "center", flexShrink: 0, fontSize: 10, fontWeight: 500, color: "#6B7280", background: "#F7F7F8", borderRadius: 6, padding: "0 8px", border: "1px solid #EFEFEF", lineHeight: 1 }}>
                              {chip}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {/* ── Countdown timer ── */}
                      {bus.listingMode !== "timetableOnly" && timerProps && (
                        <div style={{ marginTop: 6 }}>
                          <span style={{ height: 24, display: "inline-flex", alignItems: "center", padding: "0 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: "#FFF7ED", color: "#B45309", border: "1px solid #FDE68A", lineHeight: 1 }}>
                            <BookingDeadlineTimer {...timerProps} />
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Price */}
                    <div style={{ flexShrink: 0, textAlign: "right", alignSelf: "center" }}>
                      {hasStrike && (
                        <div style={{ fontSize: 9, color: "#D1D5DB", textDecoration: "line-through", marginBottom: 1 }}>
                          Rs. {bus.originalPrice}
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "baseline", gap: 2, justifyContent: "flex-end" }}>
                        <span style={{ fontSize: 10, fontWeight: 500, color: "#DC2626" }}>Rs.</span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: "#DC2626", letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                          {displayPrice}
                        </span>
                      </div>
                      <div style={{ fontSize: 9, color: "#B0B7C3", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500 }}>
                        Onwards
                      </div>
                    </div>
                  </div>

                </div>
              </button>
            </motion.div>
          );
        })}
      </motion.div>
    );
  };
  // --------------------------------------------------------------------------------------
  // END RENDER CARDS
  // --------------------------------------------------------------------------------------

  return (
    <div className="flex flex-col min-h-screen" style={{ background: "linear-gradient(160deg, #f8f0f0 0%, #f1e8e8 15%, #ede8ee 35%, #e8eaf2 55%, #eceef5 75%, #eef1f7 100%)" }}>
      <style>{`
        @keyframes rbMarquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .rb-mobile-chip-scroll::-webkit-scrollbar {
          display: none;
        }
        .rb-mobile-chip-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
      {/* ── Sticky top bar ── */}
      <div className="sticky top-0 z-10" style={{ background: "#fff", borderBottom: "1px solid #F0F0F0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <div className="flex items-center px-3 py-2.5" style={{ gap: 8 }}>
          <button
            onClick={() => nav(-1)}
            style={{ width: 34, height: 34, borderRadius: 10, border: "1px solid #F0F0F0", background: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}
            aria-label="Go back"
          >
            <FaChevronLeft style={{ fontSize: 13, color: "#374151" }} />
          </button>

          <button
            onClick={() => setMobileSearchOpen(true)}
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 12, background: "#FAFAFA", border: "1px solid #F0F0F0", cursor: "pointer", textAlign: "left" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#111", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                {from && to ? `${from} → ${to}` : "Search route"}
              </span>
              <span style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.2 }}>
                {searchDate ? getReadableDate(searchDate) : "Select Date"}
              </span>
            </div>
            <FaPen style={{ color: "#9CA3AF", fontSize: 12, flexShrink: 0, marginLeft: 8 }} />
          </button>

          {/* Quick Date Change */}
          <button
            onClick={() => setCalOpen(true)}
            style={{ width: 34, height: 34, borderRadius: 10, border: "1px solid #F0F0F0", background: "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}
            aria-label="Change date"
          >
            <FaClock style={{ fontSize: 13, color: "#9CA3AF" }} />
          </button>

          <input
            ref={mobileDateInputRef}
            type="date"
            value={searchDate}
            min={todayStr}
            onChange={onNativeDateChange}
            className="absolute opacity-0 pointer-events-none"
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>
      </div>

      {/* ── Filter/Chip bar ── */}
      <div className="sticky z-10" style={{ top: 58, background: "#fff", borderBottom: "1px solid #F0F0F0" }}>
        <div className="max-w-7xl mx-auto px-3">
          <SpecialNoticesSection />
          <div className="flex gap-2 overflow-x-auto pb-2 pt-2" style={{ scrollbarWidth: "none" }}>
            {/* Filter & Sort button */}
            <button
              onClick={() => setIsFilterOpen(true)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                height: 32, padding: "0 12px",
                borderRadius: 10, border: "1px solid #E5E7EB",
                background: "#fff", fontSize: 12, fontWeight: 500,
                color: "#374151", whiteSpace: "nowrap", flexShrink: 0,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)", cursor: "pointer",
              }}
              aria-label="Open Filter & Sort"
            >
              <FaSlidersH style={{ fontSize: 13 }} />
              Filter & Sort
              {activeFilterCount > 0 && (
                <span style={{ marginLeft: 2, fontSize: 10, fontWeight: 700, color: "#fff", background: "#DC2626", borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Time slot chips */}
            {timeSlotKeys.map((slot) => {
              const Icon = slotIcon(slot);
              const active = !!filters.timeSlots[slot];
              return (
                <button
                  key={slot}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      timeSlots: { ...prev.timeSlots, [slot]: !prev.timeSlots[slot] },
                    }))
                  }
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    height: 32, padding: "0 12px",
                    borderRadius: 10, border: `1px solid ${active ? "#DC2626" : "#EBEBEB"}`,
                    background: active ? "#FEF2F2" : "#fff",
                    fontSize: 12, fontWeight: 500,
                    color: active ? "#DC2626" : "#374151",
                    whiteSpace: "nowrap", flexShrink: 0,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                  aria-pressed={active}
                >
                  <Icon style={{ fontSize: 14 }} />
                  {slot}
                </button>
              );
            })}

            {/* AC/Non-AC chips */}
            {["AC", "Non-AC"].map((type) => {
              const active = filters.type === type;
              return (
                <button
                  key={type}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      type: prev.type === type ? "" : type,
                    }))
                  }
                  style={{
                    height: 32, padding: "0 12px",
                    borderRadius: 10, border: `1px solid ${active ? "#DC2626" : "#EBEBEB"}`,
                    background: active ? "#FEF2F2" : "#fff",
                    fontSize: 12, fontWeight: 500,
                    color: active ? "#DC2626" : "#374151",
                    whiteSpace: "nowrap", flexShrink: 0,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                  aria-pressed={active}
                >
                  {type}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Main bus list ── */}
      <div className="flex-1 w-full pb-6 pt-3">
        <div className="max-w-7xl mx-auto px-3">
          <AnimatePresence>{renderCards()}</AnimatePresence>
        </div>
      </div>

      {/* Filter Panel (Left Side Sheet) */}
      <AnimatePresence>
        {isFilterOpen && (
          <>
            {/* Backdrop */}
            <motion.button
              type="button"
              aria-label="Close filters"
              onClick={() => setIsFilterOpen(false)}
              className="fixed inset-0 bg-black/40 z-[120]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            {/* Panel */}
            <motion.div
              className="fixed top-0 left-0 bottom-[env(safe-area-inset-bottom)] w-[88%] max-w-sm bg-white z-[120] overflow-hidden rounded-r-2xl shadow-xl"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {/* Pass necessary props to FilterPanel */}
              <FilterPanel
                isMobile
                sortBy={sortBy}
                setSortBy={setSortBy}
                filters={filters}
                setFilters={setFilters}
                onClose={() => setIsFilterOpen(false)}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Other Sheets/Modals */}
      <MobileBottomSheet />
      <MobileSearchSheet />
      <MobileCityPicker
        open={mobilePickerOpen}
        mode={mobilePickerMode}
        options={mobilePickerMode === "from" ? fromOptions : toOptions}
        recent={recent}
        onPick={handleMobilePick}
        onClose={() => setMobilePickerOpen(false)}
      />
      <MobileCalendarSheet
        open={calOpen}
        value={searchDate}
        minDateString={todayStr}
        onPick={(d) => {
          setSearchDate(d);
          updateSearchWithDate(d); // Update URL/trigger new search
          setCalOpen(false);
        }}
        onClose={() => setCalOpen(false)}
      />
    </div>
  );
}

